import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useUserRegisterByCustomRegister,
  useUserUpdateByCustomRegister,
} from "../../../../hooks/users";
import {
  CustomRegistrationFormData,
  ProcessDataConfig,
  RegistrationType,
  SocialAuthResponse,
} from "../types";
import {
  prepareRegistrationPayload,
  prepareUpdatePayload,
  createBasicAuthFromAccessToken,
  forgetBookingType,
  toPublicFormId,
} from "../utils";
import { usePrivateUploadFile } from "../../../../hooks/cdn";
import PaymentModuleService from "../../../../services/paymentModule";
import { appendUtmParamsToUrl, getStoredUtmParams } from "../utmCapture";
import isSafeRedirectUrl from "../../../../utils/safeRedirect";
import { withoutSecrets } from "../../../../utils/addressSecrets";
import {
  rememberRegistrationEnd,
  requestBookingHandover,
} from "../../../../hooks/agenda/publicBooking";
import { refusalMessageKey } from "../../../../utils/refusalMessageKey";
import { SAVE_REFUSALS, SIGN_UP_REFUSALS } from "../registrationRefusals";

interface UseRegistrationSubmitProps {
  config?: ProcessDataConfig;
  type: RegistrationType;
  entityIdentification?: string;
  socialResponse: SocialAuthResponse | null;
  onSuccess: () => void;
  resolvedFormId?: string;
  /** The appointment type whose page sent the client here: they go back to it once registered. */
  bookingTypeId?: string | null;
}

export const useRegistrationSubmit = ({
  config,
  type,
  entityIdentification,
  socialResponse,
  onSuccess,
  resolvedFormId,
  bookingTypeId,
}: UseRegistrationSubmitProps) => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();
  const { processId, "*": formId } = useParams<{
    processId: string;
    "*": string;
  }>();
  const effectiveFormId = resolvedFormId || formId || undefined;
  const getUtmParams = useCallback(
    () => getStoredUtmParams(processId || ""),
    [processId]
  );
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingAnswers, setIsSavingAnswers] = useState(false);
  const { mutate: registerBylink, isLoading: isRegistering } =
    useUserRegisterByCustomRegister();
  const { mutate: updateData, isLoading: isUpdating } =
    useUserUpdateByCustomRegister();
  const { mutate: uploadFile, isLoading: isUploading } = usePrivateUploadFile({
    notify: false,
  });

  const endPageOf = useCallback(
    (userId: string, entityId: string) => {
      if (config?.postRegistrationAction === "redirect" && config.redirectUrl) {
        const url = new URL(config.redirectUrl);
        url.searchParams.append("user_identification", userId);
        url.searchParams.append("entity_identification", entityId);
        appendUtmParamsToUrl(url, getUtmParams());
        return url.toString();
      }
      return `${window.location.origin}/registration-complete?user_identification=${userId}&entity_identification=${entityId}`;
    },
    [config, getUtmParams]
  );

  /** Sends the client on to book; false when the tunnel cannot take them, and the registration ends here. */
  const handOverToBooking = useCallback(
    async (socialResponse: SocialAuthResponse, entityId: string) => {
      const formId = toPublicFormId(effectiveFormId);
      if (processId) forgetBookingType(processId);
      try {
        const url = await requestBookingHandover({
          entityId,
          formId,
          token: createBasicAuthFromAccessToken(socialResponse),
          typeId: bookingTypeId,
        });
        rememberRegistrationEnd(
          entityId,
          formId,
          endPageOf(socialResponse.user_identification, entityId)
        );
        // The completed form is no page to come back to: going back from the tunnel would send it again.
        navigate(withoutSecrets(url), { replace: true });
        return true;
      } catch (error) {
        console.error("Failed to hand the client over to booking:", error);
        toast.warn(t("customRegistration.page.toast.bookingUnavailable"));
        return false;
      }
    },
    [effectiveFormId, endPageOf, navigate, t, processId, bookingTypeId]
  );

  const handlePostRegistrationAction = useCallback(
    async (socialResponse?: any) => {
      if (
        (bookingTypeId ||
          (config?.booking?.enabled && config.booking.type_id)) &&
        socialResponse?.user_identification &&
        entityIdentification &&
        (await handOverToBooking(socialResponse, entityIdentification))
      ) {
        return;
      }
      if (
        config?.paymentConfig?.enabled &&
        socialResponse.user_identification &&
        entityIdentification
      ) {
        try {
          toast.info(t("customRegistration.page.toast.registrationSuccess"));

          const paymentData = {
            user_identification: socialResponse.user_identification,
            form_id: toPublicFormId(effectiveFormId),
            redirect_url: endPageOf(
              socialResponse.user_identification,
              entityIdentification
            ),
          };

          const response =
            await PaymentModuleService.createPaymentRequestForRegistration({
              entityIdentification: entityIdentification,
              data: paymentData,
              token: createBasicAuthFromAccessToken(socialResponse),
            });

          const paymentLink = response?.data?.element?.payment_link;
          if (paymentLink && isSafeRedirectUrl(paymentLink)) {
            setTimeout(() => {
              window.location.href = paymentLink;
            }, 1000);
          } else {
            console.error("Payment link not found in response:", response);
            toast.error(t("customRegistration.page.toast.paymentLinkFailed"));
            onSuccess();
          }
        } catch (error: any) {
          console.error("Failed to create payment request:", error);
          toast.error(
            t(
              error?.response?.status === 400
                ? "customRegistration.page.toast.paymentUnavailable"
                : "customRegistration.page.toast.paymentLinkFailed"
            )
          );
          onSuccess();
        }
      } else if (config?.postRegistrationAction === "toast") {
        onSuccess();
      } else if (
        config?.postRegistrationAction === "redirect" &&
        config.redirectUrl
      ) {
        setTimeout(() => {
          const url = new URL(config.redirectUrl);

          if (socialResponse.user_identification) {
            url.searchParams.append(
              "user_identification",
              socialResponse.user_identification
            );
          }

          if (entityIdentification) {
            url.searchParams.append(
              "entity_identification",
              entityIdentification
            );
          }

          appendUtmParamsToUrl(url, getUtmParams());
          const finalUrl = url.toString();
          if (isSafeRedirectUrl(finalUrl)) {
            window.location.href = finalUrl;
          } else {
            toast.success(
              t("customRegistration.page.toast.registrationSuccess")
            );
          }
        }, 1500);
      } else {
        toast.success(t("customRegistration.page.toast.registrationSuccess"));
      }
    },
    [
      config,
      onSuccess,
      t,
      entityIdentification,
      effectiveFormId,
      getUtmParams,
      endPageOf,
      handOverToBooking,
      bookingTypeId,
    ]
  );

  const onSubmit = useCallback(
    async (data: CustomRegistrationFormData) => {
      try {
        const { payload, answers, fileQuestions } = prepareRegistrationPayload(
          data,
          config
        );
        const answersWithFiles = { ...answers };

        if (fileQuestions && fileQuestions.length > 0 && entityIdentification) {
          for (const fileQuestion of fileQuestions) {
            const formData = new FormData();
            formData.append("file", fileQuestion.file);

            try {
              const uploadResult = await new Promise<any>((resolve, reject) => {
                uploadFile(
                  {
                    user_identification:
                      socialResponse?.user_identification || "temp",
                    entity_identification: entityIdentification,
                    data: formData,
                    token: createBasicAuthFromAccessToken(socialResponse),
                  },
                  {
                    onSuccess: (data: any) => resolve(data),
                    onError: (error: any) => reject(error),
                  }
                );
              });

              if (uploadResult?.data?.element[0]?.file) {
                answersWithFiles[fileQuestion.question] = {
                  file_name: uploadResult?.data?.element[0]?.file,
                  extension: uploadResult?.data?.element[0]?.extension,
                };
              }
            } catch (uploadError: any) {
              console.error(
                `Failed to upload file for question: ${fileQuestion.question}`,
                uploadError
              );
              toast.error(
                t("customRegistration.page.step3.uploadFailed", {
                  name: fileQuestion.file.name,
                })
              );
              throw uploadError;
            }
          }
        }

        const answersWithUtm = { ...answersWithFiles, ...getUtmParams() };

        if (socialResponse) {
          const input = prepareUpdatePayload(data);

          await updateData(
            {
              entity_identification: entityIdentification,
              user_identification: socialResponse.user_identification,
              token: createBasicAuthFromAccessToken(socialResponse),
              form_id: effectiveFormId,
              data: {
                data: input,
                answers: answersWithUtm,
              },
            },
            {
              onSuccess: async () =>
                await handlePostRegistrationAction(socialResponse),
            }
          );
        } else if (type === "email") {
          await registerBylink(
            {
              entity_identification: entityIdentification,
              form_id: effectiveFormId,
              data: {
                data: payload,
                answers: answersWithUtm,
              },
            },
            {
              onSuccess: async (response: any) => {
                await handlePostRegistrationAction(response?.data?.element);
              },
              onError: (err: any) => {
                toast.error(
                  t(
                    refusalMessageKey(
                      err,
                      SIGN_UP_REFUSALS,
                      "customRegistration.page.toast.registrationFailed"
                    )
                  )
                );
              },
            }
          );
        }
      } catch (error: any) {
        console.error("Registration error:", error);
        toast.error(
          t(
            refusalMessageKey(
              error,
              SAVE_REFUSALS,
              "customRegistration.page.toast.registrationFailed"
            )
          )
        );
      }
    },
    [
      config,
      type,
      entityIdentification,
      effectiveFormId,
      socialResponse,
      registerBylink,
      updateData,
      handlePostRegistrationAction,
      t,
      uploadFile,
      getUtmParams,
    ]
  );

  const saveUserProfile = useCallback(
    async (data: CustomRegistrationFormData): Promise<void> => {
      if (!socialResponse) {
        throw new Error("User must be authenticated before saving profile");
      }

      setIsSavingProfile(true);
      try {
        const input = prepareUpdatePayload(data);

        await new Promise<void>((resolve, reject) => {
          updateData(
            {
              entity_identification: entityIdentification,
              user_identification: socialResponse.user_identification,
              token: createBasicAuthFromAccessToken(socialResponse),
              form_id: effectiveFormId,
              data: {
                data: input,
                answers: {},
                registration_complete: false,
              },
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (error: any) => {
                reject(error);
              },
            }
          );
        });
      } finally {
        setIsSavingProfile(false);
      }
    },
    [socialResponse, entityIdentification, effectiveFormId, updateData]
  );

  const saveCustomAnswers = useCallback(
    async (data: CustomRegistrationFormData): Promise<void> => {
      if (!socialResponse) {
        throw new Error("User must be authenticated before saving answers");
      }

      setIsSavingAnswers(true);
      try {
        const { answers, fileQuestions } = prepareRegistrationPayload(
          data,
          config
        );

        const answersWithFiles = { ...answers };

        if (fileQuestions && fileQuestions.length > 0 && entityIdentification) {
          for (const fileQuestion of fileQuestions) {
            const formData = new FormData();
            formData.append("file", fileQuestion.file);

            try {
              const uploadResult = await new Promise<any>((resolve, reject) => {
                uploadFile(
                  {
                    user_identification: socialResponse.user_identification,
                    entity_identification: entityIdentification,
                    data: formData,
                    token: createBasicAuthFromAccessToken(socialResponse),
                  },
                  {
                    onSuccess: (data: any) => resolve(data),
                    onError: (error: any) => reject(error),
                  }
                );
              });

              if (uploadResult?.data?.element[0]?.file) {
                answersWithFiles[fileQuestion.question] = {
                  file_name: uploadResult?.data?.element[0]?.file,
                  extension: uploadResult?.data?.element[0]?.extension,
                };
              }
            } catch (uploadError: any) {
              console.error(
                `Failed to upload file for question: ${fileQuestion.question}`,
                uploadError
              );
              toast.error(
                t("customRegistration.page.step3.uploadFailed", {
                  name: fileQuestion.file.name,
                })
              );
              throw uploadError;
            }
          }
        }

        const answersWithUtm = { ...answersWithFiles, ...getUtmParams() };

        await new Promise<void>((resolve, reject) => {
          updateData(
            {
              entity_identification: entityIdentification,
              user_identification: socialResponse.user_identification,
              token: createBasicAuthFromAccessToken(socialResponse),
              form_id: effectiveFormId,
              data: {
                data: {},
                answers: answersWithUtm,
                registration_complete: true,
              },
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (error: any) => {
                reject(error);
              },
            }
          );
        });

        await handlePostRegistrationAction(socialResponse);
      } finally {
        setIsSavingAnswers(false);
      }
    },
    [
      socialResponse,
      entityIdentification,
      effectiveFormId,
      config,
      updateData,
      uploadFile,
      handlePostRegistrationAction,
      t,
      getUtmParams,
    ]
  );

  return {
    onSubmit,
    saveUserProfile,
    saveCustomAnswers,
    isSubmitting:
      isRegistering ||
      isUpdating ||
      isUploading ||
      isSavingProfile ||
      isSavingAnswers,
    isSavingProfile,
    isSavingAnswers,
  };
};
